/**
 * Card activity log — read hooks.
 *
 *   useCardActivity(kind, id)   → newest-first infinite list, keyset cursor
 *   useCardActivityItem(id)     → ONE row with its full bodies
 *
 * List rows omit rich-text bodies (`hasBody`); the description diff fetches
 * the single item on demand, and that cache entry is immutable by
 * construction (a logged change never changes), so it never refetches.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { apiClient } from '@/api/client'
import type { CardActivityItem, CardActivityPage, DeliverableKind } from '@/api/types-bridge'

/** Prefix for every activity list — what realtime frames and reverts invalidate. */
export const CARD_ACTIVITY_QUERY_ROOT = ['card-activity'] as const

export const CARD_ACTIVITY_QUERY_KEY = (kind: DeliverableKind, id: string) =>
  ['card-activity', kind, id] as const

export const CARD_ACTIVITY_ITEM_QUERY_KEY = (activityId: string) =>
  ['card-activity-item', activityId] as const

const PAGE_SIZE = 50

function listUrl(kind: DeliverableKind, id: string, cursor: string | null): string {
  const params = new URLSearchParams({ limit: String(PAGE_SIZE) })
  if (cursor) params.set('cursor', cursor)
  return `/api/cards/${kind}/${id}/activity?${params.toString()}`
}

export function useCardActivity(
  kind: DeliverableKind,
  id: string,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    queryKey: CARD_ACTIVITY_QUERY_KEY(kind, id),
    queryFn: async ({ pageParam }) => {
      const res = await apiClient<{ data: CardActivityPage }>(listUrl(kind, id, pageParam))
      return res.data
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: enabled && Boolean(id),
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export function useCardActivityItem(activityId: string | null, { enabled = true } = {}) {
  return useQuery({
    queryKey: CARD_ACTIVITY_ITEM_QUERY_KEY(activityId ?? ''),
    queryFn: async () => {
      const res = await apiClient<{ data: CardActivityItem }>(`/api/cards/activity/${activityId}`)
      return res.data
    },
    enabled: enabled && Boolean(activityId),
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  })
}
